/**
 * RefundForm — the refund drawer's pre-submit form (BON-147, BON-335).
 *
 * Promoted out of `RefundBookingDrawer` as a sibling of `RefundReceiptPanel`
 * for the same reason that panel was: the drawer could not absorb a
 * destination group, a reference field and per-destination copy under the
 * file's max-lines cap. This is a real refactor, not a line-shave — the
 * destination choice, the reference field and the footer's copy are all new.
 *
 * Takes a single props object rather than positional arguments: the promoted
 * function was already at 10 positional parameters in the drawer and would
 * have reached 14 once the destination, reference and their handlers joined
 * it — past the point where the call site is readable without the signature
 * open alongside it.
 *
 * The helpers below stay plain (lowercase) functions, matching the drawer's
 * own convention: each is used exactly once, from this file's one component.
 */
import type { RefundMethod, BookingDetail } from '../../api/bookingsV2';

import { formatCents, seatsNote } from './bookingDetailModel';
import { RefundDestinationChoice, type UnavailableReasons } from './RefundDestinationChoice';
import { moneyBreakdown } from './refundMoneyRows';
import { takesReference } from './useRefundBookingSubmit';

function passCountLabel(seatCount: number): string {
  return seatCount === 1 ? '1 gift pass' : `${seatCount} gift passes`;
}

function reasonField(
  value: string,
  onChange: (v: string) => void,
  error: string | null,
  inputRef: React.RefObject<HTMLTextAreaElement>,
) {
  return (
    <div>
      <label
        htmlFor="refund-reason"
        className="block text-sm font-semibold text-bark-900 dark:text-stone-100"
      >
        Reason <span className="text-red-600">*</span>
      </label>
      <textarea
        id="refund-reason"
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        maxLength={500}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? 'refund-reason-error refund-reason-help' : 'refund-reason-help'}
        placeholder="Why is this being refunded? Whatever the customer told you."
        className="mt-1.5 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm dark:border-stone-600 dark:bg-[#1e2520] dark:text-stone-100"
      />
      <p id="refund-reason-help" className="mt-1.5 text-xs text-stone-500 dark:text-stone-400">
        Saved to this booking&rsquo;s history for staff. The customer never sees it.
      </p>
      {/* role="alert" so the failure is announced rather than silently
          appearing, and tied to the field via aria-describedby above — the
          pattern ReservationContactFields already uses for exactly this. */}
      {error && (
        <p id="refund-reason-error" role="alert" className="mt-1.5 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

/** Only rendered for the two destinations the money didn't come from Nebari
 *  minting anything — the one tie-back an imported booking can carry. */
function referenceField(value: string, onChange: (v: string) => void) {
  return (
    <div>
      <label
        htmlFor="refund-reference"
        className="block text-sm font-semibold text-bark-900 dark:text-stone-100"
      >
        Reference <span className="font-normal text-stone-500 dark:text-stone-400">(optional)</span>
      </label>
      <input
        type="text"
        id="refund-reference"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={255}
        aria-describedby="refund-reference-help"
        placeholder="Stripe refund id, check number, transfer id…"
        className="mt-1.5 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm dark:border-stone-600 dark:bg-[#1e2520] dark:text-stone-100"
      />
      <p id="refund-reference-help" className="mt-1.5 text-xs text-stone-500 dark:text-stone-400">
        Whatever ties this back to the money that already moved, if anything does.
      </p>
    </div>
  );
}

/** The copy that has to change with the destination: nothing is minted or
 *  emailed once the CSR steps off gift passes or the gift card. A pass is a
 *  seat, not money, so its button names a count rather than an amount. */
function destinationCopy(destination: RefundMethod, refundCents: number, seatCount: number) {
  if (destination === 'GIFT_PASS') {
    return {
      footerNote: 'Passes are issued now and emailed to the customer in one message.',
      buttonLabel: `Issue ${passCountLabel(seatCount)}`,
      submittingLabel: 'Issuing…',
    };
  }
  if (destination === 'GIFT_CARD') {
    return {
      footerNote: 'Gift card is issued and emailed immediately.',
      buttonLabel: `Issue gift card · ${formatCents(refundCents)}`,
      submittingLabel: 'Issuing…',
    };
  }
  return {
    footerNote:
      'Nothing is minted or emailed — record this only after the money has already moved.',
    buttonLabel: `Record refund · ${formatCents(refundCents)}`,
    submittingLabel: 'Recording…',
  };
}

function formFooter(
  copy: ReturnType<typeof destinationCopy>,
  canSubmit: boolean,
  submitting: boolean,
  onCancel: () => void,
) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-stone-200 px-6 py-4 dark:border-stone-700">
      <p className="text-xs text-stone-500 dark:text-stone-400">{copy.footerNote}</p>
      <div className="flex flex-shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="rounded-lg border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-700 hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-stone-600 dark:text-stone-200 dark:hover:bg-stone-700"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="rounded-lg bg-moss-600 px-4 py-2 text-sm font-semibold text-white hover:bg-moss-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? copy.submittingLabel : copy.buttonLabel}
        </button>
      </div>
    </div>
  );
}

/** What the customer gets back: seats as passes, or the net cash. */
function refundSummary(booking: BookingDetail, destination: RefundMethod, refundCents: number) {
  const seatCount = booking.money.ticketsCount;
  if (destination === 'GIFT_PASS') {
    return (
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
          Passes
        </p>
        <p className="rounded-lg border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-bark-900 dark:border-stone-700 dark:bg-[#1a201d] dark:text-stone-100">
          {passCountLabel(seatCount)}, one per seat, whatever the seats were paid with.
        </p>
        <p className="mt-2 text-xs text-stone-500 dark:text-stone-400">{seatsNote(seatCount)}</p>
      </div>
    );
  }
  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
        Amount
      </p>
      {moneyBreakdown(booking.money, refundCents)}
      <p className="mt-2 text-xs text-stone-500 dark:text-stone-400">{seatsNote(seatCount)}</p>
    </div>
  );
}

export interface RefundFormProps {
  booking: BookingDetail;
  refundCents: number;
  /** Null while the setting loads. */
  giftPassesEnabled: boolean | null;
  destination: RefundMethod;
  onDestinationChange: (method: RefundMethod) => void;
  unavailableReasons: UnavailableReasons;
  reason: string;
  onReasonChange: (v: string) => void;
  reference: string;
  onReferenceChange: (v: string) => void;
  error: string | null;
  submitting: boolean;
  canSubmit: boolean;
  reasonRef: React.RefObject<HTMLTextAreaElement>;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

export function RefundForm({
  booking,
  refundCents,
  giftPassesEnabled,
  destination,
  onDestinationChange,
  unavailableReasons,
  reason,
  onReasonChange,
  reference,
  onReferenceChange,
  error,
  submitting,
  canSubmit,
  reasonRef,
  onSubmit,
  onCancel,
}: RefundFormProps) {
  return (
    <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-6">
        <RefundDestinationChoice
          value={destination}
          onChange={onDestinationChange}
          unavailableReasons={unavailableReasons}
          disabled={submitting}
          giftPassesEnabled={giftPassesEnabled}
          seatCount={booking.money.ticketsCount}
        />
        {reasonField(reason, onReasonChange, error, reasonRef)}
        {takesReference(destination) && referenceField(reference, onReferenceChange)}
        {refundSummary(booking, destination, refundCents)}
      </div>
      {formFooter(
        destinationCopy(destination, refundCents, booking.money.ticketsCount),
        canSubmit,
        submitting,
        onCancel,
      )}
    </form>
  );
}
